import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { merge } from 'lodash';
import CloseModal from 'assets/svgs/close.svg';
import ArrowLeftBack from 'assets/svgs/arrowLeftBack.svg';
import 'shared/components/modal/Modal.scss';
import './ModalInfo.scss';

const fullHeightStyles = {
  overlay: {
    overflow: 'auto',
    alignItems: 'unset',
  },
  content: {
    margin: '3% 0',
    maxHeight: 'none',
    height: 'max-content',
  },
};

const ModalInfo = ({
  modalIsOpen,
  closeModal = () => {},
  className = '',
  headerTitle = '',
  cancelLabel = 'common.labels.cancel',
  saveLabel = 'common.labels.save',
  onSubmit,
  onCancel,
  children = [],
  subHeaderContent,
  showCancel,
  loadingButton = false,
  scrollableContentId,
  disabledSubmit = false,
  disableClose = false,
  tooltipContent,
  containerClass = '',
  headerSticky = false,
  fullHeightModal = false,
  headerBack = false,
  customContainerStyles = {},
  wideModal = false,
  iconActionButton = null,
  hasRemoveButton = false,
  bottomButtonLabel = false,
  showBottomButton = false,
  hideButtons = false,
}) => {
  const { t } = useTranslation();
  const modalContentRef = useRef(null);
  const scrollDownRef = useRef(null);
  const scrollHeaderRef = useRef(null);
  const classHeader = headerSticky || fullHeightModal ? 'modal-header-sticky' : '';

  Array.from(document.querySelectorAll('.btn-submit-container')).forEach((el) => {
    const popover = bootstrap.Popover.getOrCreateInstance(el);
    if (disabledSubmit) {
      popover.enable();
    } else {
      popover.disable();
    }
  });

  const setFocus = () => {
    modalContentRef.current?.focus();
  };

  const scrollDown = () => {
    const element = modalContentRef.current;
    if (element) {
      element.scrollTop = element.scrollHeight - element.clientHeight;
    }
  };

  const checkScroll = () => {
    const element = modalContentRef.current;
    if (element) {
      if (element.scrollHeight > element.clientHeight + element.scrollTop + 20) {
        if (scrollDownRef.current) scrollDownRef.current.style.display = 'block';
      } else if (scrollDownRef.current) {
        scrollDownRef.current.style.display = 'none';
      }
    }

    if (headerTitle && modalContentRef.current) {
      const headerElement = modalContentRef.current;
      if (headerElement.scrollTop > 0) {
        if (scrollHeaderRef.current) {
          scrollHeaderRef.current.style.boxShadow = '0 0 15px 0 rgb(0 0 0 / 12%)';
        }
      } else if (scrollHeaderRef.current) {
        scrollHeaderRef.current.style.boxShadow = 'none';
      }
    }
  };

  const addActions = () => {
    setFocus();
    checkScroll();
    Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]')).forEach((el) => {
      const tooltip = bootstrap.Tooltip.getOrCreateInstance(el);
      tooltip.enable();
    });
    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]')).forEach((el) =>
      bootstrap.Popover.getOrCreateInstance(el).enable()
    );
    Array.from(document.querySelectorAll('.btn-submit-container')).forEach((el) => {
      const popover = bootstrap.Popover.getOrCreateInstance(el);
      if (disabledSubmit) {
        popover.enable();
      } else {
        popover.disable();
      }
    });

    if (fullHeightModal) {
      const element = document.getElementsByClassName('ReactModal__Overlay');
      element[0].scroll(0, 0);
    }
  };

  const setCustomStyles = () => {
    const customStyles = {};
    merge(customStyles, customContainerStyles, fullHeightModal ? fullHeightStyles : {});

    return customStyles;
  };

  const modalWidth = wideModal ? 'wide-modal' : '';

  return (
    <Modal
      isOpen={modalIsOpen}
      style={setCustomStyles()}
      onRequestClose={closeModal}
      className={`modal-info ${modalWidth} ${
        fullHeightModal ? 'full-height-modal' : ''
      } ${containerClass}`}
      shouldCloseOnOverlayClick={false}
      contentLabel="Modal"
      onAfterOpen={addActions}
      ariaHideApp={false}>
      <div className={`${className} modal-info-container`}>
        {headerTitle && (
          <div
            className={`modal-header ${classHeader} ${headerBack ? 'header-with-back' : ''}`}
            ref={scrollHeaderRef}>
            {headerBack && (
              <button className="btn-link btn-back" onClick={onCancel || closeModal}>
                <ArrowLeftBack /> {t('common.labels.back')}
              </button>
            )}
            <p className="fw-bold align-text-bottom d-inline-block mb-0">{t(headerTitle)}</p>
            <button
              className={`close-blue text-navy btn-light ${disableClose ? 'disabled' : ''}`}
              aria-label="Close"
              onClick={closeModal}
              disabled={disableClose}>
              <CloseModal />
            </button>
          </div>
        )}
        <div
          className="modal-content"
          tabIndex="0"
          ref={modalContentRef}
          onScroll={checkScroll}
          onClick={checkScroll}
          id={scrollableContentId}>
          {!!subHeaderContent && subHeaderContent}
          <div className="modal-content-inner">{children}</div>
        </div>
        {hideButtons || showBottomButton ? null : showCancel ? (
          <div className="button-section">
            <button className="btn btn-outline btn-cancel" onClick={onCancel || closeModal}>
              {t(cancelLabel)}
            </button>
            <span
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-trigger="hover"
              data-bs-content={tooltipContent}
              className="btn-submit-container">
              <button
                disabled={loadingButton || disabledSubmit}
                className={`btn ${hasRemoveButton ? 'btn-remove' : 'btn-action'}`}
                onClick={onSubmit}>
                {iconActionButton}
                {loadingButton ? <i className="fa fa-spinner fa-spin" /> : t(saveLabel)}
              </button>
            </span>
          </div>
        ) : (
          <div className="button-section">
            <span
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-trigger="hover"
              data-bs-content={tooltipContent}
              className="btn-submit-container">
              <button
                disabled={loadingButton || disabledSubmit}
                className={`btn ${hasRemoveButton ? 'btn-remove' : 'btn-action'}`}
                onClick={onSubmit}>
                {iconActionButton}
                {loadingButton ? <i className="fa fa-spinner fa-spin" /> : t(saveLabel)}
              </button>
            </span>
          </div>
        )}
        {showBottomButton && (
          <div className="button-section">
            <button className="btn btn-action" onClick={closeModal}>
              {t(bottomButtonLabel) || t('common.labels.cancel')}
            </button>
          </div>
        )}
        <div className="scroll-down" onClick={scrollDown} ref={scrollDownRef}>
          {t('common.labels.scrollDown')}
          <div>
            <i className="fa fa-angle-down" />
          </div>
        </div>
      </div>
    </Modal>
  );
};

ModalInfo.propTypes = {
  closeModal: PropTypes.func,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  modalIsOpen: PropTypes.bool.isRequired,
  headerTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  cancelLabel: PropTypes.string,
  saveLabel: PropTypes.string,
  children: PropTypes.node,
  showCancel: PropTypes.bool,
  className: PropTypes.string,
  containerClass: PropTypes.string,
  loadingButton: PropTypes.bool,
  disabledSubmit: PropTypes.bool,
  hideButtons: PropTypes.bool,
  disableClose: PropTypes.bool,
  headerSticky: PropTypes.bool,
  fullHeightModal: PropTypes.bool,
  headerBack: PropTypes.bool,
  customContainerStyles: PropTypes.shape({}),
  wideModal: PropTypes.bool,
  subHeaderContent: PropTypes.node,
  scrollableContentId: PropTypes.string,
  tooltipContent: PropTypes.string,
  iconActionButton: PropTypes.node,
  hasRemoveButton: PropTypes.bool,
  bottomButtonLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  showBottomButton: PropTypes.bool,
};

export default ModalInfo;
